import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, catchError, EMPTY, map, Observable, of } from 'rxjs'
import { SessionStorageService } from 'src/app/login/services/session-storage.service'
import { SelectedInstallation } from 'src/app/shared/models/selected-installation.model'
import { ObservableCacheService } from 'src/app/shared/services/observable-cache.service'
import { RedirectTo } from 'src/app/utils/redirect-to'
import {
    BusinessProfileService as ApiBusinessProfileService,
    BusinessProfileCustomerInstallationDetailsResponse,
    EditBusinessProfilePrimaryContactRequest,
    GetEanInvoiceStatusResponse,
    ProfileListItem,
    UpdateBusinessProfileEanInvoiceRequest,
    UpdateInvoiceViaEmailRequest,
} from '../../../shared/services/backend'
import { ProductionEquipmentAccessService } from './production-equipment-access.service'

@Injectable()
export class BusinessProfileService {
    private errors: BehaviorSubject<any> = new BehaviorSubject<any>(null)
    public errors$: Observable<any> = this.errors.asObservable()

    // Cache configuration
    private readonly CACHE_DURATION_MS = 15 * 60 * 1000 // 15 minutes

    constructor(
        private productionEquipmentAccessService: ProductionEquipmentAccessService,
        private sessionStorageService: SessionStorageService,
        private profileService: ApiBusinessProfileService,
        private router: Router,
        private cache: ObservableCacheService
    ) {}

    public async setSelectedInstallation(installation: SelectedInstallation) {
        this.sessionStorageService.setSelectedInstallation(installation)
        // Show/hide production equipment from main menu, only for business customers
        await this.productionEquipmentAccessService.getProductionEquipmentAccess()
    }

    public getInstallations(forceRefresh: boolean = false): Observable<Array<ProfileListItem>> {
        const cacheKey = 'business-installations'
        const shouldForceRefresh = forceRefresh || this.cache.clearIfStoredValueIsEmptyArray(cacheKey)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.profileService.apiProfileBusinessGet().pipe(
                    map(
                        (response) =>
                            response.items.map((item) => ({
                                customerNumber: item.customerNumber,
                                installations: item.installations,
                            })) as ProfileListItem[]
                    ),
                    catchError((error: any) => {
                        this.handleError(error)
                        // Clear cache on error
                        this.cache.clear(cacheKey)
                        return EMPTY
                    })
                ),
            { forceRefresh: shouldForceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears the installations cache, forcing the next call to fetch fresh data
     */
    public clearInstallationsCache(): void {
        this.cache.clear('business-installations')
    }

    public getAssociatedUsersInformation(
        customerNumber: string,
        installationNumber: string,
        redirectOnError: boolean = true,
        forceRefresh: boolean = false
    ): Observable<BusinessProfileCustomerInstallationDetailsResponse | null> {
        const cacheKey = this.cache.createKey('business-associatedUsers', customerNumber, installationNumber)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.profileService
                    .apiProfileBusinessCustomerCustomerNumberInstallationInstallationNumberGet(
                        customerNumber,
                        installationNumber
                    )
                    .pipe(
                        catchError((error: any) => {
                            if (redirectOnError) {
                                this.handleError(error)
                            }
                            // Remove failed cache entry
                            this.cache.clear(cacheKey)
                            return of(null)
                        })
                    ),
            { forceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears a specific associated users cache entry
     */
    public clearAssociatedUsersCache(customerNumber: string, installationNumber: string): void {
        const cacheKey = this.cache.createKey('business-associatedUsers', customerNumber, installationNumber)
        this.cache.clear(cacheKey)
    }

    /**
     * Clears all associated users cache entries
     */
    public clearAllAssociatedUsersCache(): void {
        this.cache.clearByPattern(/^business-associatedUsers:/)
    }

    public updatePrimaryContact(
        customerNumber: string,
        request: EditBusinessProfilePrimaryContactRequest
    ): Observable<any> {
        return this.profileService
            .apiProfileBusinessCustomerCustomerNumberPrimaryContactPut(customerNumber, request, 'response', true)
            .pipe(
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    public updateInvoiceEmail(customerNumber: string, request: UpdateInvoiceViaEmailRequest): Observable<any> {
        return this.profileService
            .apiProfileBusinessCustomerCustomerNumberInvoicePut(customerNumber, request, 'response', true)
            .pipe(
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    public getEanStatus(
        customerNumber: string,
        forceRefresh: boolean = false
    ): Observable<GetEanInvoiceStatusResponse | null> {
        const cacheKey = this.cache.createKey('business-eanStatus', customerNumber)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.profileService.apiProfileBusinessCustomerCustomerNumberEanInvoiceStatusGet(customerNumber).pipe(
                    catchError((_: any) => {
                        this.cache.clear(cacheKey)
                        return of(null)
                    })
                ),
            { forceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears EAN status cache for a specific customer
     */
    public clearEanStatusCache(customerNumber: string): void {
        const cacheKey = this.cache.createKey('business-eanStatus', customerNumber)
        this.cache.clear(cacheKey)
    }

    public updateEanInvoice(customerNumber: string, request: UpdateBusinessProfileEanInvoiceRequest): Observable<any> {
        return this.profileService
            .apiProfileBusinessCustomerCustomerNumberEanInvoicePut(customerNumber, request, 'response', true)
            .pipe(
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    public deleteEanInvoice(customerNumber: string): Observable<any> {
        return this.profileService
            .apiProfileBusinessCustomerCustomerNumberEanInvoiceDelete(customerNumber, 'response', true)
            .pipe(
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    private handleError(error: any) {
        RedirectTo.internalServerError(this.router, error)
    }
}
